/*! Copyright [Amazon.com](http://amazon.com/), Inc. or its affiliates. All Rights Reserved.
SPDX-License-Identifier: Apache-2.0 */
import ErrorMessage from "@aws-northstar/ui/components/CognitoAuth/components/ErrorMessage";
import {
  ButtonDropdown,
  Container,
  ContentLayout,
  Header,
} from "@cloudscape-design/components";
import { useContext, useEffect, useState } from "react";
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";
import { RuntimeConfigContext } from "../../components/RuntimeContext";
import { WebSocketExplorer } from "../../components/WebSocketApiExplorer";
// eslint-disable-next-line @typescript-eslint/no-require-imports

/**
 * Component to render the home ApiExplorer.
 */
const ApiExplorer: React.FC = () => {
  const runtimeContext = useContext(RuntimeConfigContext);
  const [apiSpec, setApiSpec] = useState<{ [apiName: string]: any }>({});
  const [selectedApi, setSelectedApi] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  // Load the OpenAPI spec
  useEffect(() => {
    void (async () => {
      try {
        setApiSpec(
          Object.fromEntries([
            ...(
              await Promise.all(
                Object.keys(runtimeContext?.typeSafeApis ?? {}).map(
                  async (apiName) => [
                    apiName,
                    await fetch(`/${apiName}/api.json`).then((res) =>
                      res.json(),
                    ),
                  ],
                ),
              )
            ).map(([apiName, _apiSpec]) => [
              apiName,
              {
                ..._apiSpec,
                servers: [{ url: runtimeContext?.typeSafeApis[apiName] }],
              },
            ]),
            ...(
              await Promise.all(
                Object.keys(runtimeContext?.typeSafeWebSocketApis ?? {}).map(
                  async (apiName) => [
                    apiName,
                    await fetch(`/${apiName}/api.json`).then((res) =>
                      res.json(),
                    ),
                  ],
                ),
              )
            ).map(([apiName, _apiSpec]) => [
              apiName,
              {
                ..._apiSpec,
                url: runtimeContext?.typeSafeWebSocketApis[apiName],
                __websocket: true,
              },
            ]),
          ]),
        );
        setSelectedApi(
          Object.keys({
            ...runtimeContext?.typeSafeApis,
            ...runtimeContext?.typeSafeWebSocketApis,
          })[0],
        );
      } catch (e) {
        setError(
          "No OpenAPI definition detected. Ensure TypeSafeAPI(s) are passed in to Cloudscape Website construct.",
        );
      }
    })();
  }, []);

  return error ? (
    <ErrorMessage>{error}</ErrorMessage>
  ) : selectedApi ? (
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            <ButtonDropdown
              variant="primary"
              onItemClick={(e) => setSelectedApi(e.detail.id!)}
              items={[
                ...(Object.keys(runtimeContext?.typeSafeApis).length > 0
                  ? [
                      {
                        text: "REST",
                        items: Object.keys(runtimeContext?.typeSafeApis).map(
                          (apiName) => ({
                            text: apiName,
                            id: apiName,
                            disabled: selectedApi === apiName,
                          }),
                        ),
                      },
                    ]
                  : []),
                ...(Object.keys(runtimeContext?.typeSafeWebSocketApis).length >
                0
                  ? [
                      {
                        text: "WEBSOCKET",
                        items: Object.keys(
                          runtimeContext?.typeSafeWebSocketApis,
                        ).map((apiName) => ({
                          text: apiName,
                          id: apiName,
                          disabled: selectedApi === apiName,
                        })),
                      },
                    ]
                  : []),
              ]}
            >
              {selectedApi}
            </ButtonDropdown>
          }
        >
          API Explorer
        </Header>
      }
    >
      {apiSpec[selectedApi]?.__websocket ? (
        <WebSocketExplorer
          selectedApi={selectedApi}
          spec={apiSpec[selectedApi]}
        />
      ) : (
        <Container>
          <SwaggerUI spec={apiSpec[selectedApi]} />
        </Container>
      )}
    </ContentLayout>
  ) : (
    <></>
  );
};

export default ApiExplorer;
